"use client"

import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { cn } from "@/lib/utils"
import Image from 'next/image'
import { KeyboardEvent, useCallback, useEffect, useState } from 'react'

interface ProductGalleryProps {
  images: string[];
  productName: string;
}

export function ProductGallery({ images, productName }: ProductGalleryProps): React.ReactNode {
  const [activeIndex, setActiveIndex] = useState(0)
  const [api, setApi] = useState<CarouselApi | null>(null)
  const [lightboxApi, setLightboxApi] = useState<CarouselApi | null>(null)

  const onSelect = useCallback(() => {
    if (!api) return
    setActiveIndex(api.selectedScrollSnap())
  }, [api])

  useEffect(() => {
    if (!api) return
    onSelect()
    api.on('select', onSelect)
    return () => {
      api.off('select', onSelect)
    }
  }, [api, onSelect])

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const openLightbox = (index: number): void => {
    setLightboxIndex(index)
    if (lightboxApi) {
      lightboxApi.scrollTo(index)
    }
  }

  const closeLightbox = (): void => {
    setLightboxIndex(null)
  }

  useEffect(() => {
    if (!lightboxApi || lightboxIndex === null) return
    lightboxApi.scrollTo(lightboxIndex, true)
  }, [lightboxApi, lightboxIndex])

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, action: () => void): void => {
    if (e.key === 'Enter') {
      action()
    }
  }

  return (
    <>
      <div className="space-y-4">
        <Carousel
          className="w-full"
          opts={{
            align: 'start',
            loop: false,
          }}
          setApi={setApi}
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <div
                  className="aspect-square relative bg-gray-100 rounded-lg overflow-hidden"
                  onClick={() => { openLightbox(index) }}
                  onKeyDown={(e) => { handleKeyDown(e, () => { openLightbox(index) }) }}
                  role="button"
                  tabIndex={0}
                >
                  <Image
                    src={image}
                    alt={`${productName} ${index + 1}`}
                    fill
                    priority={index === 0}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-contain cursor-pointer"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>

        <div className="grid grid-cols-4 gap-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => {
                if (api) api.scrollTo(index)
              }}
              className={cn(
                "aspect-square relative rounded-md overflow-hidden",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                "transition-all duration-200",
                activeIndex === index ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary/50"
              )}
              aria-label={`View image ${index + 1} of ${images.length}`}
              aria-pressed={activeIndex === index}
            >
              <Image
                src={image}
                alt={`${productName} thumbnail ${index + 1}`}
                fill
                sizes="(max-width: 768px) 25vw, 12vw"
                className={cn(
                  "object-contain transition-opacity duration-200",
                  activeIndex === index ? "opacity-100" : "opacity-70 hover:opacity-100"
                )}
              />
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div
            className="absolute w-full h-full cursor-pointer"
            onClick={closeLightbox}
            onKeyDown={(e) => { handleKeyDown(e, closeLightbox) }}
            role="button"
            tabIndex={0}
          />
          <Carousel
            className="w-[90vw]"
            opts={{
              align: 'start',
              loop: false,
            }}
            setApi={setLightboxApi}
          >
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div
                    className="aspect-square relative bg-gray-100 rounded-lg overflow-hidden h-[90vh] w-full"
                  >
                    <Image
                      src={image}
                      alt={`${productName} ${index + 1}`}
                      fill
                      priority={index === 0}
                      className="object-contain"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel >

          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white cursor-pointer">
            Close
          </button>
        </div>
      )}
    </>
  )
}